import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import EditIcon from '@mui/icons-material/Edit'
import EventIcon from '@mui/icons-material/Event'
import EventBusyIcon from '@mui/icons-material/EventBusy'
import LockIcon from '@mui/icons-material/Lock'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import { blue, grey } from '@mui/material/colors'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Popover from '@mui/material/Popover'
import Typography from '@mui/material/Typography'
import { format, getDay } from 'date-fns'
import noop from 'lodash/noop'
import React, { useCallback, useMemo } from 'react'

import { useAccountingLockValue } from 'providers/AccountingLockProvider'
import { BACKEND_DATE_FORMAT_DATE_FNS } from 'settings/constants/date'
import T from 'T'

import type { RentalPeriodApi } from 'types/api/rental-fee/rentalPeriod'

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

const PERIOD_CONTEXT = {
  CLOSED: 'closed',
  ONGOING: 'ongoing',
  OUTSIDE: 'outside',
}

const LockedInfo = ({ text }: { text: string }) => (
  <Box alignItems="center" display="flex" gap={1} sx={{ color: grey[600] }}>
    <LockIcon fontSize="small" />
    <Typography variant="body2">{text}</Typography>
  </Box>
)

type PeriodContext = 'closed' | 'ongoing' | 'outside'

type DraftInvoiceInfo = {
  invoiceIds: string[]
  invoiceNumbers: number[]
}

type FinalizedInvoiceInfo = {
  invoiceIds: string[]
  invoiceEntries: { invoiceNumber: number; invoiceStatus: string | null }[]
}

type RentalFeeDayPopoverProps = {
  open?: boolean
  anchorEl?: HTMLElement | null
  anchorPosition?: { top: number; left: number } | null
  date?: Date | null
  isStartDate?: boolean
  isEndDate?: boolean
  hasStartDate?: boolean
  canSetStartDate?: boolean
  canSetEndDate?: boolean
  isStartDateDeletable?: boolean
  isEndDateDeletable?: boolean
  periodContext?: PeriodContext | null
  period?: RentalPeriodApi | null
  activePeriodId?: string | null
  canUndoReset?: boolean
  canDeletePeriod?: boolean
  hasOrphanedBilledDays?: boolean
  draftInvoiceInfo?: DraftInvoiceInfo | null
  finalizedInvoiceInfo?: FinalizedInvoiceInfo | null
  onClose?: () => void
  onClearStartDate?: () => void
  onClearEndDate?: () => void
  onSetStartDate?: (date: Date) => void
  onSetEndDate?: (date: Date) => void
  onResetGracePeriod?: (date: Date) => void
  onUndoReset?: (date: Date) => void
  onEnterEditPeriod?: (period: RentalPeriodApi) => void
  onDeleteRentalPeriod?: (period: RentalPeriodApi) => void
}

const RentalFeeDayPopover = ({
  open = false,
  anchorEl = null,
  anchorPosition = null,
  date,
  isStartDate = false,
  isEndDate = false,
  hasStartDate = false,
  canSetStartDate = true,
  canSetEndDate = true,
  isStartDateDeletable = true,
  isEndDateDeletable = true,
  periodContext = null,
  period = null,
  activePeriodId = null,
  canUndoReset = false,
  canDeletePeriod = false,
  hasOrphanedBilledDays = false,
  draftInvoiceInfo = null,
  finalizedInvoiceInfo = null,
  onClose = noop,
  onClearStartDate = noop,
  onClearEndDate = noop,
  onSetStartDate = noop,
  onSetEndDate = noop,
  onResetGracePeriod = noop,
  onUndoReset = noop,
  onEnterEditPeriod = noop,
  onDeleteRentalPeriod = noop,
}: RentalFeeDayPopoverProps) => {
  const hasAnchor = Boolean(anchorEl || anchorPosition)
  const isOpen = open && hasAnchor

  const { checkIsLocked } = useAccountingLockValue()
  const isAccountingLocked = useMemo(() => {
    if (!date || !(date instanceof Date)) return false
    return checkIsLocked(format(date, BACKEND_DATE_FORMAT_DATE_FNS))
  }, [date, checkIsLocked])

  const dayOfWeek = useMemo(() => {
    if (!date || !(date instanceof Date)) return ''
    return DAYS_OF_WEEK[getDay(date)]
  }, [date])

  const dayNumber = useMemo(() => {
    if (!date || !(date instanceof Date)) return ''
    return format(date, 'd')
  }, [date])

  const handleSetStartDate = useCallback(() => {
    if (date) {
      onSetStartDate(date)
    }
    onClose()
  }, [date, onSetStartDate, onClose])

  const handleSetEndDate = useCallback(() => {
    if (date) {
      onSetEndDate(date)
    }
    onClose()
  }, [date, onSetEndDate, onClose])

  const handleClearStartDate = useCallback(() => {
    onClearStartDate()
    onClose()
  }, [onClearStartDate, onClose])

  const handleClearEndDate = useCallback(() => {
    onClearEndDate()
    onClose()
  }, [onClearEndDate, onClose])

  const handleResetGracePeriod = useCallback(() => {
    if (date) {
      onResetGracePeriod(date)
    }
    onClose()
  }, [date, onResetGracePeriod, onClose])

  const handleUndoReset = useCallback(() => {
    if (date) {
      onUndoReset(date)
    }
    onClose()
  }, [date, onUndoReset, onClose])

  const handleEnterEditPeriod = useCallback(() => {
    if (period) {
      onEnterEditPeriod(period)
    }
  }, [period, onEnterEditPeriod])

  const handleDeleteRentalPeriod = useCallback(() => {
    if (period) {
      onDeleteRentalPeriod(period)
    }
    onClose()
  }, [period, onDeleteRentalPeriod, onClose])

  const isWithinClosedPeriod = periodContext === PERIOD_CONTEXT.CLOSED
  const isWithinOngoingPeriod = periodContext === PERIOD_CONTEXT.ONGOING
  const isOutsidePeriods = periodContext === PERIOD_CONTEXT.OUTSIDE || periodContext === null
  const showEditPeriodEntry = isWithinClosedPeriod || isWithinOngoingPeriod
  const isEditingThisPeriod = period && activePeriodId === period.id
  const showEditPeriodRangeContent = showEditPeriodEntry && isEditingThisPeriod
  const showOngoingRangeContent = !isStartDate && isWithinOngoingPeriod && !showEditPeriodRangeContent
  const showPeriodEntryActions = showEditPeriodEntry && period && !isEditingThisPeriod
  const showOrphanedInvoiceWarning = showEditPeriodEntry && period && hasOrphanedBilledDays
  const showNewPeriodContent = isOutsidePeriods && !isStartDate && !isEndDate
  const showStartDateContent = isStartDate && !isWithinClosedPeriod && !isWithinOngoingPeriod
  const showEndDateContent = isEndDate && !isWithinClosedPeriod
  const showUndoResetContent = isStartDate && isEndDate && !isAccountingLocked && canUndoReset
  const showSetEndDateAction = showOngoingRangeContent || (showStartDateContent && !isEndDate && canSetEndDate)

  return (
    <Popover
      anchorEl={anchorPosition ? undefined : anchorEl}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      anchorPosition={anchorPosition || undefined}
      anchorReference={anchorPosition ? 'anchorPosition' : 'anchorEl'}
      open={isOpen}
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            minWidth: 180,
            boxShadow: 3,
            maxWidth: 320,
            position: 'relative',
          },
        },
      }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      onClose={onClose}
    >
      <Box sx={{ p: 2 }}>
        <Box alignItems="flex-start" display="flex" justifyContent="space-between">
          <Box flex={1} textAlign="center">
            <Typography color="textSecondary" variant="body2">
              {dayOfWeek}
            </Typography>
            <Typography fontWeight={600} variant="h4">
              {dayNumber}
            </Typography>
          </Box>
          <IconButton size="small" sx={{ ml: 1, position: 'absolute', right: 4, top: 4 }} onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {showEditPeriodRangeContent && (
          <Typography color="text.secondary" sx={{ mb: 1 }} variant="body2">
            {T.EDIT_PERIOD_DATES_HINT}
          </Typography>
        )}

        {isAccountingLocked && (
          <Box display="flex" flexDirection="column" gap={1.5}>
            <Alert severity="info" sx={{ mb: 1.5 }}>
              <LockedInfo text={T.BILLING_LOCKED} />
            </Alert>
          </Box>
        )}

        <Box display="flex" flexDirection="column" gap={1.5}>
          {!isAccountingLocked && showOrphanedInvoiceWarning && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              <Typography fontWeight={600} variant="body2">
                {T.INVOICE_NO_LONGER_EXISTS}
              </Typography>
              <Typography color="text.secondary" variant="caption">
                {T.BILLED_DAYS_REFERENCE_DELETED_INVOICE}
              </Typography>
            </Alert>
          )}

          {!isAccountingLocked && showPeriodEntryActions && (
            <Button fullWidth color="primary" startIcon={<EditIcon />} variant="outlined" onClick={handleEnterEditPeriod}>
              {T.EDIT}
            </Button>
          )}

          {draftInvoiceInfo && draftInvoiceInfo.invoiceNumbers.length > 0 && (
            <Box
              sx={{
                mb: 1.5,
                p: 1.5,
                borderRadius: 1,
                border: `1px solid ${blue[100]}`,
                bgcolor: blue[50],
              }}
            >
              <Box alignItems="center" display="flex" gap={0.75}>
                <DescriptionOutlinedIcon fontSize="small" sx={{ color: blue[500] }} />
                <Typography fontWeight={600} variant="body2">
                  {draftInvoiceInfo.invoiceNumbers.join(', ')}
                </Typography>
                <Chip color="info" label={T.DRAFT} size="small" sx={{ borderRadius: 1 }} />
              </Box>
              <Typography color="text.secondary" sx={{ mt: 0.5, display: 'block', pl: 3.25 }} variant="caption">
                {T.DRAFT_INVOICE_WILL_REGENERATE}
              </Typography>
            </Box>
          )}

          {!isAccountingLocked && showPeriodEntryActions && finalizedInvoiceInfo && finalizedInvoiceInfo.invoiceEntries.length > 0 && (
            <Box
              sx={{
                mb: 1.5,
                p: 1.5,
                borderRadius: 1,
                border: `1px solid ${blue[100]}`,
                bgcolor: blue[50],
              }}
            >
              {finalizedInvoiceInfo.invoiceEntries.map(({ invoiceNumber, invoiceStatus }) => (
                <Box key={invoiceNumber} alignItems="center" display="flex" gap={0.75} mb={0.5}>
                  <DescriptionOutlinedIcon fontSize="small" sx={{ color: blue[500] }} />
                  <Typography fontWeight={600} variant="body2">
                    {invoiceNumber}
                  </Typography>
                  <Chip color="info" label={invoiceStatus} size="small" sx={{ borderRadius: 1 }} />
                </Box>
              ))}
              <Typography color="text.secondary" sx={{ display: 'block', pl: 3.25 }} variant="caption">
                {T.CANNOT_CHANGE_BILLED_DAYS}
              </Typography>
            </Box>
          )}

          {!isAccountingLocked && showPeriodEntryActions && canDeletePeriod && (
            <Button fullWidth color="error" startIcon={<DeleteOutlineIcon />} variant="outlined" onClick={handleDeleteRentalPeriod}>
              {T.DELETE_RENTAL_PERIOD}
            </Button>
          )}

          {!isAccountingLocked && showSetEndDateAction && (
            <Button fullWidth color="error" startIcon={<EventBusyIcon />} variant="outlined" onClick={handleSetEndDate}>
              {T.SET_END_DATE}
            </Button>
          )}

          {!isAccountingLocked && (showNewPeriodContent || showEditPeriodRangeContent) && (
            <Box display="flex" flexDirection="column" gap={1.5}>
              {canSetStartDate && (
                <Button
                  fullWidth
                  color="primary"
                  startIcon={<EventIcon />}
                  sx={{
                    borderColor: blue[500],
                    color: blue[500],
                    '&:hover': {
                      borderColor: blue[600],
                      backgroundColor: blue[50],
                    },
                  }}
                  variant="outlined"
                  onClick={handleSetStartDate}
                >
                  {T.SET_START_DATE}
                </Button>
              )}
              {(hasStartDate || showEditPeriodRangeContent) && canSetEndDate && (
                <Button fullWidth color="error" startIcon={<EventBusyIcon />} variant="outlined" onClick={handleSetEndDate}>
                  {T.SET_END_DATE}
                </Button>
              )}
            </Box>
          )}

          {!isAccountingLocked && showStartDateContent && isStartDateDeletable && (
            <Button fullWidth color="inherit" sx={{ color: 'text.secondary' }} variant="text" onClick={handleClearStartDate}>
              {T.CLEAR_START_DATE}
            </Button>
          )}

          {!isAccountingLocked && showEndDateContent && isEndDateDeletable && (
            <Button fullWidth color="error" startIcon={<EventBusyIcon />} variant="outlined" onClick={handleClearEndDate}>
              {T.CLEAR_END_DATE}
            </Button>
          )}

          {!isAccountingLocked && showUndoResetContent && (
            <>
              <Button fullWidth color="warning" startIcon={<RestartAltIcon />} variant="outlined" onClick={handleUndoReset}>
                {T.UNDO_RESET}
              </Button>
            </>
          )}

          {!isAccountingLocked && !isOutsidePeriods && !showUndoResetContent && (
            <Button fullWidth color="warning" startIcon={<RestartAltIcon />} variant="outlined" onClick={handleResetGracePeriod}>
              {T.RESET_GRACE_PERIOD}
            </Button>
          )}
          <Button fullWidth color="inherit" sx={{ color: 'text.secondary' }} variant="text" onClick={onClose}>
            {T.CANCEL}
          </Button>
        </Box>
      </Box>
    </Popover>
  )
}

export default RentalFeeDayPopover
